import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services'
import { queryKeys, invalidateQueries } from '@/lib/query'
import { useOrg } from '@/context/OrgContext'
import { historyQueryKeys } from './useHistory'
import type { Report, ChecklistItem, Asset } from '@/types'

export interface ReportFilters {
  buildingId?: string
  roomId?: string
}

export interface CreateReportData {
  building_id: string
  room_id: string
  checklist: ChecklistItem[]
  notes?: string
  gps_latitude?: number
  gps_longitude?: number
}

export interface UpdateReportData {
  checklist?: ChecklistItem[]
  notes?: string
}

/**
 * Hook to fetch all reports with optional filters.
 */
export function useReports(filters?: ReportFilters) {
  const { effectiveOrgId } = useOrg()
  const params = new URLSearchParams()
  if (filters?.buildingId) params.set('building_id', filters.buildingId)
  if (filters?.roomId) params.set('room_id', filters.roomId)
  const queryString = params.toString()

  return useQuery({
    queryKey: queryKeys.reports.all(filters),
    queryFn: () =>
      api.get<Report[]>(`/reports${queryString ? `?${queryString}` : ''}`),
    enabled: !!effectiveOrgId,
  })
}

/**
 * Hook to fetch a single report by ID.
 */
export function useReport(reportId: string) {
  return useQuery({
    queryKey: queryKeys.reports.detail(reportId),
    queryFn: () => api.get<Report>(`/reports/${reportId}`),
    enabled: !!reportId,
  })
}

/**
 * Hook to fetch room assets (assets assigned to a room).
 */
export function useRoomAssets(roomId: string) {
  return useQuery({
    queryKey: queryKeys.assets.byRoom(roomId),
    queryFn: () => api.get<Asset[]>(`/rooms/${roomId}/assets`),
    enabled: !!roomId,
  })
}

/**
 * Hook to create a new report.
 */
export function useCreateReport() {
  return useMutation({
    mutationFn: (data: CreateReportData) =>
      api.post<Report>('/reports', data),
    onSuccess: () => {
      invalidateQueries.reports()
    },
  })
}

/**
 * Hook to update an existing report.
 */
export function useUpdateReport(reportId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateReportData) =>
      api.put<Report>(`/reports/${reportId}`, data),
    onSuccess: (updatedReport) => {
      queryClient.setQueryData(queryKeys.reports.detail(reportId), updatedReport)
      queryClient.invalidateQueries({ queryKey: historyQueryKeys.list('reports', reportId) })
      invalidateQueries.reports()
    },
  })
}
